import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { isNil } from 'lodash-es';
import { Highlight, themes } from 'prism-react-renderer';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  prism: {
    margin: 0,
    padding: theme.spacing(1),
    overflow: 'auto',
    borderRadius: theme.shape.borderRadius,
  },
}));

type Props = {
  isLoading?: boolean;
  prompt?: string;
  response: string;
  open: boolean;
  onClose: () => void;
};

export const PromptResponseDialog = ({
  isLoading = false,
  prompt,
  response,
  open,
  onClose,
}: Props) => {
  const { classes, theme, cx } = useStyles();

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>Details</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ mt: '20px' }} // centers the spinner vertically
          >
            <CircularProgress size={48} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {!isNil(prompt) && (
              <Highlight
                code={prompt}
                language="yaml"
                theme={
                  theme.palette.mode === 'light'
                    ? themes.github
                    : themes.oceanicNext
                }
              >
                {({
                  className,
                  style,
                  tokens,
                  getLineProps,
                  getTokenProps,
                }) => (
                  <pre className={cx(className, classes.prism)} style={style}>
                    {tokens.map((line, i) => (
                      <Box
                        key={i}
                        {...getLineProps({ line })}
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {line.map((token, key) => (
                          <span key={key} {...getTokenProps({ token })} />
                        ))}
                      </Box>
                    ))}
                  </pre>
                )}
              </Highlight>
            )}
            <Highlight
              code={response}
              language="yaml"
              theme={
                theme.palette.mode === 'light'
                  ? themes.github
                  : themes.oceanicNext
              }
            >
              {({ className, style, tokens, getLineProps, getTokenProps }) => (
                <pre className={cx(className, classes.prism)} style={style}>
                  {tokens.map((line, i) => (
                    <Box
                      key={i}
                      {...getLineProps({ line })}
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </Box>
                  ))}
                </pre>
              )}
            </Highlight>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
